import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Car, User, Clock, MapPin, CheckCircle, Loader, CreditCard, Phone, FileText } from 'lucide-react';
import { vehiclesAPI, handleApiError } from '../services/api';
import { useFlashMessage } from '../components/FlashMessage';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPhoneNumber } from '../utils/phoneFormatter';

const VehicleStatusUpdate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [parkingSpot, setParkingSpot] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();

  // Staff yetkisi kontrolü - SADECE STAFF ERİŞEBİLİR
  const hasAccess = user && (
    user.isStaff ||
    user.user_type === 'staff'
  );

  useEffect(() => {
    if (hasAccess) {
      loadVehicle();
    }
  }, [id, hasAccess]);

  const loadVehicle = async () => {
    setLoading(true);
    try {
      // Kullanıcının lokasyonlarından araçları getir
      if (user?.locations && user.locations.length > 0) {
        for (const location of user.locations) {
          try {
            const response = await vehiclesAPI.getActiveVehicles(location.id);
            if (response.data.success) {
              const foundVehicle = response.data.data.find(v => v.id === parseInt(id));
              if (foundVehicle) {
                setVehicle(foundVehicle);
                setSelectedStatus(foundVehicle.status || '');
                setParkingSpot(foundVehicle.parking_spot || '');
                setNewAmount(Math.floor(foundVehicle.actual_amount || 0).toString());
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.warn(`Failed to load vehicle from location ${location.id}:`, error);
          }
        }

        // Aktif araçlarda bulunamadıysa teslim edilmiş araçlarda ara
        for (const location of user.locations) {
          try {
            const response = await vehiclesAPI.getDeliveredVehicles(location.id);
            if (response.data.success) {
              const foundVehicle = response.data.data.find(v => v.id === parseInt(id));
              if (foundVehicle) {
                setVehicle(foundVehicle);
                setSelectedStatus(foundVehicle.status || '');
                setParkingSpot(foundVehicle.parking_spot || '');
                setNewAmount(Math.floor(foundVehicle.actual_amount || 0).toString());
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.warn(`Failed to load delivered vehicle from location ${location.id}:`, error);
          }
        }
      }

      showDanger('Araç bulunamadı.');
      navigate('/vehicles');
    } catch (error) {
      console.error('Failed to load vehicle:', error);
      showDanger('Araç yüklenirken hata oluştu: ' + handleApiError(error));
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcının lokasyonlarına göre mevcut servis türlerini kontrol et
  const getAvailableStatusOptions = () => {
    // Teslim edilmiş araçlar için özel seçenekler
    if (vehicle?.status === 'delivered') {
      return [
        { value: 'received', label: 'Teslim Alındı', color: 'bg-blue-100 text-blue-800' },
        { value: 'ready', label: 'Hazır', color: 'bg-green-100 text-green-800' },
        { value: 'parked', label: 'Park Edildi', color: 'bg-purple-100 text-purple-800' }
      ];
    }

    const allStatusOptions = [
      { value: 'received', label: 'Teslim Alındı', color: 'bg-blue-100 text-blue-800' },
      { value: 'washing', label: 'Yıkanıyor', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'parked', label: 'Park Edildi', color: 'bg-purple-100 text-purple-800' },
      { value: 'ready', label: 'Hazır', color: 'bg-green-100 text-green-800' },
      { value: 'called', label: 'Çağrıldı', color: 'bg-orange-100 text-orange-800' },
      { value: 'delivered', label: 'Teslim Edildi', color: 'bg-gray-100 text-gray-800' }
    ];

    if (!user?.locations || !vehicle) {
      return allStatusOptions;
    }

    // Aracın bulunduğu lokasyonu bul
    let vehicleLocation = user.locations.find(loc => loc.id === parseInt(vehicle.location_id));

    // Eğer vehicle.location_id yoksa ve kullanıcının sadece bir lokasyonu varsa onu kullan
    if (!vehicleLocation && user.locations.length === 1) {
      vehicleLocation = user.locations[0];
    }

    if (!vehicleLocation) {
      return allStatusOptions;
    }

    // Lokasyon türüne göre filtreleme - sadece mevcut lokasyonda desteklenen servis türleri
    const availableStatuses = new Set(['received', 'ready', 'delivered']); // Her zaman mevcut olan durumlar

    // Sadece bu lokasyonda mevcut olan servisleri ekle
    if (vehicleLocation.type === 'wash') {
      availableStatuses.add('washing');
    } else if (vehicleLocation.type === 'valet') {
      availableStatuses.add('parked');
      availableStatuses.add('called');
    } else if (vehicleLocation.type === 'both') {
      availableStatuses.add('washing');
      availableStatuses.add('parked');
      availableStatuses.add('called');
    }

    const filteredOptions = allStatusOptions.filter(option => availableStatuses.has(option.value));

    return filteredOptions;
  };

  const statusOptions = vehicle ? getAvailableStatusOptions() : [];

  const getServiceTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale Hizmeti';
      default: return type;
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) {
      showDanger('Lütfen bir durum seçin.');
      return;
    }

    setSubmitLoading(true);

    try {
      const statusData = {
        status: selectedStatus,
        parking_spot: parkingSpot.trim() || null,
        actual_amount: parseInt(newAmount) || 0
      };

      await vehiclesAPI.updateVehicleStatus(vehicle.id, statusData);

      showSuccess(`Araç durumu "${statusOptions.find(s => s.value === selectedStatus)?.label}" olarak güncellendi.`);
      navigate('/vehicles');
    } catch (error) {
      console.error('Status update error:', error);
      showDanger('Durum güncellenirken hata oluştu: ' + handleApiError(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        <p className="text-gray-500 text-sm">Kullanıcı türü: {user?.user_type}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Araç bilgileri yükleniyor...</span>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Araç bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/vehicles')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Araçlara Dön
        </button>
      </div>

      {/* Submit Buttons - At Top */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => navigate('/vehicles')}
          className="btn-secondary"
          disabled={submitLoading}
        >
          İptal
        </button>
        <button
          onClick={handleStatusUpdate}
          disabled={submitLoading || !selectedStatus || (selectedStatus === vehicle.status && parseInt(newAmount) === Math.floor(vehicle.actual_amount || 0))}
          className="btn-primary flex items-center"
        >
          {submitLoading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Güncelleniyor...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Güncelle
            </>
          )}
        </button>
      </div>

      {/* Vehicle Info Card */}
      <div className="card">
        <div className="card-header bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f]">
          <h2 className="text-lg font-semibold text-white">
            {vehicle?.status === 'delivered' ? 'Teslim Edilmiş Araç Durumu' : 'Araç Durumu Güncelle'}
          </h2>
        </div>
        <div className="card-body space-y-8">
          {/* PRIMARY UPDATE FIELDS - Emphasized - EN ÜSTTE */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#7a2c2c] flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Durum
              </h3>
              {vehicle.reference_number && (
                <div className="bg-[#7a2c2c] text-white px-4 py-2 rounded-lg font-bold text-lg shadow-md">
                  CS{vehicle.reference_number}
                </div>
              )}
            </div>

            {/* Status Selection */}
            <div>
              {vehicle?.status === 'delivered' && (
                <p className="text-sm text-orange-600 mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                  Teslim edilmiş araç için müşteri vazgeçtiği durumda yeni durum seçebilirsiniz
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedStatus(option.value)}
                    className={`py-3 px-4 rounded-lg border-2 text-sm font-semibold transition-all ${
                      selectedStatus === option.value
                        ? `border-[#7a2c2c] scale-105 shadow-md ${option.color}`
                        : 'border-gray-200 hover:border-[#d4a5a5] bg-white'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Update - Large & Emphasized */}
            <div>
              <label className="block text-base font-semibold text-[#7a2c2c] mb-3">
                <CreditCard className="w-5 h-5 inline mr-2" />
                Tutar (₺)
              </label>
              <input
                type="number"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                placeholder="0"
                className="form-input w-full text-lg h-14"
                min="0"
                step="1"
              />
            </div>
          </div>

          {/* Vehicle Details - Emphasized */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-6">
              <div className="h-16 w-16 bg-[#7a2c2c] rounded-full flex items-center justify-center shadow-md">
                <Car className="h-8 w-8 text-white" />
              </div>
              <div className="ml-4">
                <div className="text-xl font-bold text-gray-900">{vehicle.plate_number}</div>
                <div className="text-sm text-gray-600 font-medium">{vehicle.brand} {vehicle.model}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Müşteri - Sadece gerçek müşteri adı varsa göster */}
              {vehicle.customer_name && vehicle.customer_name !== 'Veribil' && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <User className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Müşteri</div>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.customer_name}</span>
                  </div>
                </div>
              )}

              {/* Telefon - Sadece gerçek telefon varsa göster */}
              {vehicle.customer_phone && vehicle.customer_phone !== '00000000000' && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <Phone className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Telefon</div>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatPhoneNumber(vehicle.customer_phone)}
                    </span>
                  </div>
                </div>
              )}

              {/* Giriş Zamanı - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <Clock className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Giriş Zamanı</div>
                  <span className="text-sm font-semibold text-gray-900">{new Date(vehicle.check_in).toLocaleString('tr-TR')}</span>
                </div>
              </div>

              {/* Çıkış Zamanı - Sadece teslim edilmişse göster */}
              {vehicle.check_out && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <Clock className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Çıkış Zamanı</div>
                    <span className="text-sm font-semibold text-gray-900">{new Date(vehicle.check_out).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}

              {/* Hizmet Türü - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <Car className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Hizmet Türü</div>
                  <span className="text-sm font-semibold text-gray-900">{getServiceTypeText(vehicle.service_type)}</span>
                </div>
              </div>

              {/* Tutar - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <CreditCard className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Tutar</div>
                  <span className="text-sm font-semibold text-gray-900">₺{parseFloat(vehicle.actual_amount || 0).toFixed(0)}</span>
                </div>
              </div>

              {/* Park Yeri - Sadece belirtilmişse göster */}
              {vehicle.parking_spot && (
                <div className="flex items-center md:col-span-2 bg-white rounded-lg p-3 shadow-sm">
                  <MapPin className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Park Yeri</div>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.parking_spot}</span>
                  </div>
                </div>
              )}

              {/* Not - Sadece varsa göster */}
              {vehicle.parking_note && (
                <div className="flex items-start md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 shadow-sm">
                  <div className="flex-shrink-0 mr-3 mt-0.5">
                    <FileText className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-yellow-700 font-semibold mb-1">Not</div>
                    <span className="text-sm text-gray-900">{vehicle.parking_note}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECONDARY FIELDS */}
          {(selectedStatus === 'parked' || selectedStatus === 'ready') && (
            <div className="border-t-2 border-gray-300 pt-8">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-6 flex items-center">
                <span className="bg-gray-100 px-3 py-1 rounded">Ek Bilgiler</span>
              </h3>

              {/* Parking Spot */}
              <div className="mb-6">
                <label className="block text-base font-semibold text-gray-900 mb-3">
                  <MapPin className="w-5 h-5 inline mr-2 text-[#7a2c2c]" />
                  Park Yeri (Opsiyonel)
                </label>
                <input
                  type="text"
                  value={parkingSpot}
                  onChange={(e) => setParkingSpot(e.target.value)}
                  placeholder="Örn: A-15, Kat 2, Blok C-12"
                  className="form-input w-full text-lg h-14"
                  maxLength={50}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleStatusUpdate;
